/**
 * booleanUtils.js
 *
 * Shared boolean handling utilities for API response compatibility
 * Handles both integer (1/0) and native boolean (true/false) values
 * for backward compatibility during INTEGER to BOOLEAN migration
 */

/**
 * Convert value to boolean, handling both boolean and integer formats
 *
 * This utility exists for backward compatibility during the migration
 * from SQLite INTEGER (0/1) to PostgreSQL BOOLEAN (true/false).
 *
 * @param {boolean|number|null|undefined} value - Value to convert
 * @returns {boolean} True if value is truthy, false otherwise
 *
 * @example
 * toBoolean(true)        // → true
 * toBoolean(false)       // → false
 * toBoolean(1)           // → true (for backward compatibility)
 * toBoolean(0)           // → false (for backward compatibility)
 * toBoolean(null)        // → false
 * toBoolean(undefined)   // → false
 * toBoolean('yes')       // → true (any truthy string)
 */
export const toBoolean = (value) => {
  // Handle null and undefined explicitly
  if (value === null || value === undefined) {
    return false;
  }

  // Handle boolean values (native type from PostgreSQL)
  if (typeof value === 'boolean') {
    return value;
  }

  // Handle integer values (from older SQLite INTEGER format)
  if (typeof value === 'number') {
    return value !== 0; // 1 → true, 0 → false, others → based on truthiness
  }

  // Handle string values (just in case API returns string representation)
  if (typeof value === 'string') {
    const normalized = value.toLowerCase().trim();
    if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
      return true;
    }
    if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === '') {
      return false;
    }
    // Any other non-empty string is truthy
    return value.length > 0;
  }

  // For any other type, use JavaScript's built-in truthiness
  return Boolean(value);
};

/**
 * Negate a boolean value, handling both formats
 * @param {boolean|number|null|undefined} value - Value to negate
 * @returns {boolean} Negated boolean value
 *
 * @example
 * notBoolean(true)       // → false
 * notBoolean(1)          // → false
 * notBoolean(0)          // → true
 */
export const notBoolean = (value) => {
  return !toBoolean(value);
};

/**
 * Check if value is explicitly true
 * Stricter than toBoolean - only true/1 return true
 * @param {any} value - Value to check
 * @returns {boolean} True only if value is explicitly true or 1
 *
 * @example
 * isTrue(true)           // → true
 * isTrue(1)              // → true
 * isTrue(false)          // → false
 * isTrue(0)              // → false
 * isTrue(null)           // → false
 * isTrue('true')         // → false (string)
 */
export const isTrue = (value) => {
  return value === true || value === 1;
};

/**
 * Check if value is explicitly false
 * Stricter than notBoolean - only false/0 return true
 * @param {any} value - Value to check
 * @returns {boolean} True only if value is explicitly false or 0
 *
 * @example
 * isFalse(false)         // → true
 * isFalse(0)             // → true
 * isFalse(true)          // → false
 * isFalse(1)             // → false
 * isFalse(null)          // → false
 */
export const isFalse = (value) => {
  return value === false || value === 0;
};

/**
 * Convert boolean to integer for API requests (backward compatibility)
 * Converts true/false to 1/0 for APIs expecting integer format
 * @param {boolean|number} value - Boolean value to convert
 * @returns {number} 1 for true, 0 for false
 *
 * @example
 * toInteger(true)        // → 1
 * toInteger(false)       // → 0
 * toInteger(1)           // → 1
 * toInteger(0)           // → 0
 */
export const toInteger = (value) => {
  return toBoolean(value) ? 1 : 0;
};

/**
 * Format boolean value as human-readable string
 * @param {boolean|number} value - Boolean value
 * @param {object} options - Formatting options
 *   @param {string} options.trueLabel - Label for true (default: 'Yes')
 *   @param {string} options.falseLabel - Label for false (default: 'No')
 *   @param {boolean} options.capitalized - Capitalize output (default: true)
 * @returns {string} Formatted boolean string
 *
 * @example
 * formatBoolean(true)                                   // → 'Yes'
 * formatBoolean(false)                                  // → 'No'
 * formatBoolean(1)                                      // → 'Yes'
 * formatBoolean(true, { trueLabel: 'Active', falseLabel: 'Inactive' })
 *                                                       // → 'Active'
 * formatBoolean(false, { capitalized: false })         // → 'no'
 */
export const formatBoolean = (value, options = {}) => {
  const {
    trueLabel = 'Yes',
    falseLabel = 'No',
    capitalized = true
  } = options;

  const label = toBoolean(value) ? trueLabel : falseLabel;

  return capitalized
    ? label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()
    : label.toLowerCase();
};

/**
 * Safe boolean access with default fallback
 * Returns a boolean value with fallback if value is null/undefined
 * @param {any} value - Value to check
 * @param {boolean} defaultValue - Default if value is null/undefined (default: false)
 * @returns {boolean} Boolean value or default
 *
 * @example
 * safeBoolean(null, false)      // → false
 * safeBoolean(undefined, true)  // → true
 * safeBoolean(true, false)      // → true
 * safeBoolean(0, true)          // → false
 */
export const safeBoolean = (value, defaultValue = false) => {
  if (value === null || value === undefined) {
    return defaultValue;
  }
  return toBoolean(value);
};
